import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { Stack, StackProps, SxProps } from '@mui/material';
import { makeId } from '../utils/cms';

type ScrollContextType = {
    onScrollListener: (listener: (event: Event) => void) => () => void;
    onScrollEndReached: (listener: () => void) => () => void;
};

type ScrollListenerRef = { [id: string]: (event: Event) => void };
type ScrollEndReachedListenerRef = { [id: string]: () => void };

const defaultScrollContext = {
    onScrollListener: () => () => {},
    onScrollEndReached: () => () => {},
};

export const ScrollContext =
    createContext<ScrollContextType>(defaultScrollContext);

export const useScrollContext = () => {
    return useContext(ScrollContext);
};

interface ScrollContextComponentProps extends StackProps {
    sx?: SxProps;
    component?: React.ElementType;
}

export default function ScrollContextComponent({
    sx,
    children,
    component = 'main',
    ...otherProps
}: PropsWithChildren<ScrollContextComponentProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const listener = useRef<ScrollListenerRef>({});
    const onEndReachedListener = useRef<ScrollEndReachedListenerRef>({});

    useEffect(() => {
        const onScroll = (event: Event) => {
            for (const listenerId of Object.keys(listener.current)) {
                listener.current[listenerId](event);
            }

            // @ts-ignore
            const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
            if (bottom) {
                for (const listenerId of Object.keys(
                    onEndReachedListener.current,
                )) {
                    onEndReachedListener.current[listenerId]();
                }
            }
        };

        containerRef.current?.addEventListener('scroll', onScroll);
        return () =>
            containerRef.current?.removeEventListener('scroll', onScroll);
    }, []);

    const onScrollListener = (newListener: (event: Event) => void) => {
        const id = makeId(8);
        listener.current[id] = newListener;
        return () => {
            if (listener.current[id]) {
                delete listener.current[id];
            }
        };
    };

    const onScrollEndReached = (newListener: () => void) => {
        const id = makeId(8);
        onEndReachedListener.current[id] = newListener;
        return () => {
            if (onEndReachedListener.current[id]) {
                delete onEndReachedListener.current[id];
            }
        };
    };

    const value = {
        onScrollListener,
        onScrollEndReached,
    };

    return (
        <ScrollContext.Provider value={value}>
            <Stack
                ref={containerRef}
                component={component}
                sx={sx}
                {...otherProps}>
                {children}
            </Stack>
        </ScrollContext.Provider>
    );
}
